import React, { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    Box,
    Typography,
    Divider,
    ListItemIcon,
    ListItemText,
    Collapse
} from '@mui/material';
import { PowerSettingsNew, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useAuthStore, useSettingsStore } from '@core/store';
import { Role } from '@core/types';
import { useAuth } from '@core/auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { TEST_IDS } from '@core/constants/testIds';
import { SUPPORTED_LANGUAGES, Language } from '@core/constants/languages';

export const HeaderUserMenu: React.FC = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [languageExpanded, setLanguageExpanded] = useState(false);
    const { user, hasRole } = useAuthStore();
    const { selectedLocale, setLocale } = useSettingsStore();
    const { logout } = useAuth();
    const { t } = useTranslation();

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null);
            setLanguageExpanded(false);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setLanguageExpanded(false);
    };

    const handleLanguageToggle = () => {
        setLanguageExpanded(!languageExpanded);
    };

    const handleLogout = async () => {
        handleClose();
        await logout();
    };

    const changeLanguage = (language: Language) => {
        setLocale(language);
    };

    const getRoleName = (role?: Role) => {
        switch (role) {
            case Role.SuperAdmin:
                return t('superAdmin');
            case Role.OrganisationAdmin:
                return t('organisationAdmin');
            case Role.UnitAdmin:
                return t('unitAdmin');
            case Role.StoreAdmin:
                return t('storeAdmin');
            default:
                return t('user');
        }
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                size="large"
                data-testid="header-user-menu-button"
                className="header-user-menu-button"
            >
                <i className="bi bi-person-circle" style={{ fontSize: '28px' }} />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                className="header-user-menu"
            >
                {/* User Info Header */}
                <Box className="header-user-info">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'inherit', mb: 1 }}>
                        {user?.fullName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 0.5 }}>
                        {user?.email}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        {getRoleName(user?.role)}
                    </Typography>
                </Box>

                {/* Language Button */}
                <MenuItem onClick={handleLanguageToggle}>
                    <ListItemIcon>
                        <img
                            src={`/images/flags/${SUPPORTED_LANGUAGES.find((lang) => lang.locale === selectedLocale.locale)?.icon || 'en'}.png`}
                            alt="Language"
                            style={{ width: 24, height: 16, objectFit: 'cover' }}
                        />
                    </ListItemIcon>
                    <ListItemText>{t('languages')}</ListItemText>
                    {languageExpanded ? <ExpandLess /> : <ExpandMore />}
                </MenuItem>

                {/* Expandable Language Options */}
                <Collapse in={languageExpanded} timeout="auto" unmountOnExit>
                    {SUPPORTED_LANGUAGES.map((language) => (
                        <MenuItem
                            key={language.locale}
                            onClick={() => changeLanguage(language)}
                            selected={selectedLocale.locale === language.locale}
                            data-testid={TEST_IDS.HEADER.LANGUAGE_MENU_ITEM(language.locale)}
                            sx={{
                                pl: 6,
                                backgroundColor: 'transparent !important'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: '40px' }}>
                                <img
                                    src={`/images/flags/${language.icon}.png`}
                                    alt={language.name}
                                    style={{ width: 24, height: 16, objectFit: 'cover' }}
                                />
                            </ListItemIcon>
                            <ListItemText>{t(language.name)}</ListItemText>
                        </MenuItem>
                    ))}
                </Collapse>

                <Divider />

                {/* Sign Out */}
                <MenuItem onClick={handleLogout} className="logout-menu-item">
                    <ListItemIcon>
                        <PowerSettingsNew />
                    </ListItemIcon>
                    <ListItemText>{t('signOut')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};
